/**
 * environment.ts — the reflex environment (noiseless perception, immediate
 * reward).
 *
 * Ported from `ReflexEnv` in `_proto_reflex_v1.py` (lines 210-335). One step
 * = one reflex: the agent sees the raw token window around each action's
 * destination, picks an action, gets immediate reward. No gamma, no
 * bootstrap — this is a contextual bandit, not an MDP.
 *
 * Boundary (mirrors mob's World/Env split):
 *   - The TRUTH grid (mutable, long-lived) is owned by the caller — passed
 *     into the constructor. The user can paint shapes between runs.
 *   - This Env is one EPISODE: pose, discovered-stones set, step counter,
 *     done flag. Cheap to construct; `reset()` starts a run.
 *   - The agent reads features via `candidateFeatures()` and reward via
 *     `step()`; it never touches the raw truth at decision time.
 */

import {
  GRID,
  START_R,
  START_C,
  FREE,
  GOLD,
  STONE,
  N_TOKEN_VALUES,
  DIRECTIONS,
  N_ACTIONS,
  WINDOW,
  HALF_W,
  WINDOW_FEATURES,
  N_FEATURES,
  R_GOLD,
  R_BUMP_DISCOVERED,
  R_EXPLORE,
} from './constants';
import type { Grid } from './shapes';
import { cloneGrid, countToken } from './shapes';

export interface StepOutcome {
  reward: number;
  done: boolean;
}

export class ReflexEnv {
  /** Snapshot of truth at episode start (we mutate truth as gold is eaten). */
  truthInitial: Grid;
  /** Live truth (gold removed as collected). */
  truth!: Grid;
  goldLeft = 0;
  r = START_R;
  c = START_C;
  steps = 0;
  done = false;
  maxSteps = 120;
  /** Discovered stones (within window) — bumping one of these is punished. */
  discoveredStones = new Set<number>();

  constructor(truth: Grid) {
    this.truthInitial = cloneGrid(truth);
  }

  reset(): void {
    this.truth = cloneGrid(this.truthInitial);
    this.goldLeft = countToken(this.truth, GOLD);
    this.r = START_R;
    this.c = START_C;
    this.steps = 0;
    this.done = false;
    this.discoveredStones = new Set<number>();
    this.scan();
  }

  /** Total gold placed in this episode (before any was eaten). */
  get totalGold(): number {
    return countToken(this.truthInitial, GOLD);
  }

  /** Noiseless scan: record every stone in the current window. */
  scan(): void {
    for (let dr = -HALF_W; dr <= HALF_W; dr++) {
      for (let dc = -HALF_W; dc <= HALF_W; dc++) {
        const nr = this.r + dr;
        const nc = this.c + dc;
        if (nr >= 0 && nr < GRID && nc >= 0 && nc < GRID) {
          if (this.truth[nr * GRID + nc] === STONE) {
            this.discoveredStones.add(nr * GRID + nc);
          }
        }
      }
    }
  }

  /**
   * One-hot the WINDOW_FLAT × N_TOKEN_VALUES window centered at (cr, cc),
   * agent-relative. Out-of-bounds cells are treated as FREE (so the grid
   * edge behaves like a benign empty margin rather than a wall).
   */
  windowAround(cr: number, cc: number, out: Float64Array, offset: number): void {
    // out[offset .. offset + WINDOW_FEATURES) receives the 75-dim one-hot.
    for (let i = 0; i < WINDOW_FEATURES; i++) out[offset + i] = 0;
    for (let dr = -HALF_W; dr <= HALF_W; dr++) {
      for (let dc = -HALF_W; dc <= HALF_W; dc++) {
        const nr = cr + dr;
        const nc = cc + dc;
        const idx = (dr + HALF_W) * WINDOW + (dc + HALF_W);
        let tok: number;
        if (nr >= 0 && nr < GRID && nc >= 0 && nc < GRID) {
          tok = this.truth[nr * GRID + nc];
        } else {
          tok = FREE;
        }
        out[offset + idx * N_TOKEN_VALUES + tok] = 1;
      }
    }
  }

  /**
   * Stack per-action destination features into a flat Float64Array of length
   * N_ACTIONS * N_FEATURES (row-major: action-major, then feature).
   *
   * ACTION-INVARIANT layout (the load-bearing fix): each action's 75-dim
   * window lives in the SHARED columns [a*83 .. a*83+75), plus a one-hot
   * action ID at columns [a*83+75+a]. The destination token (cell 12 of the
   * window) occupies the SAME feature index for every action, so the tree
   * learns one rule "GOLD at destination -> reward" shared across all slots.
   */
  candidateFeatures(out: Float64Array): void {
    for (let a = 0; a < N_ACTIONS; a++) {
      const base = a * N_FEATURES;
      const [dr, dc] = DIRECTIONS[a];
      const destR = this.r + dr;
      const destC = this.c + dc;
      this.windowAround(destR, destC, out, base);
      out[base + WINDOW_FEATURES + a] = 1;
    }
  }

  /** Allocate and return a fresh feature buffer. Convenience for callers. */
  static makeFeatureBuffer(): Float64Array {
    return new Float64Array(N_ACTIONS * N_FEATURES);
  }

  /**
   * The true token at each of the 8 candidate destinations. Used by the
   * reflex-confusion diagnostic: the agent's choice is scored against the
   * best available token. Returns -1 for out-of-bounds (treated as STONE
   * for scoring — OOB is never the right choice).
   */
  candidateDestTokens(out: Int8Array): void {
    for (let a = 0; a < N_ACTIONS; a++) {
      const [dr, dc] = DIRECTIONS[a];
      const nr = this.r + dr;
      const nc = this.c + dc;
      if (nr < 0 || nr >= GRID || nc < 0 || nc >= GRID) out[a] = -1;
      else out[a] = this.truth[nr * GRID + nc];
    }
  }

  /**
   * Per-action validity mask — TRUE iff the destination is a move the agent
   * can usefully take:
   *   - in bounds (not OOB; the grid edge is always "known" and punished)
   *   - NOT already in `discoveredStones` (a known wall — bumping it is a
   *     punished no-op, the canonical "stuck turning" trap)
   * FREE, GOLD, and UNDISCOVERED stones all pass: gold vs free is the actual
   * learned reflex, and an undiscovered stone costs only one tick (it becomes
   * discovered, then masked on the next call). So masking preserves the
   * learning problem while removing the deterministic deadlock.
   *
   * The agent has this information for free — it's PERCEIVED, not learned
   * (the 5×5 window reveals stone locations; OOB is geometric). Feeding it
   * to the bandit as a side channel keeps the regime's "active exploitation"
   * framing intact: the bandit still has to learn GOLD > FREE among valid
   * actions.
   */
  candidateValid(out: Uint8Array): void {
    for (let a = 0; a < N_ACTIONS; a++) {
      const [dr, dc] = DIRECTIONS[a];
      const nr = this.r + dr;
      const nc = this.c + dc;
      if (nr < 0 || nr >= GRID || nc < 0 || nc >= GRID) {
        out[a] = 0;
        continue;
      }
      const key = nr * GRID + nc;
      out[a] = this.discoveredStones.has(key) ? 0 : 1;
    }
  }

  /** Immediate-reward step. Updates pose + scans. */
  step(action: number): StepOutcome {
    if (this.done) return { reward: 0, done: true };
    this.steps++;
    const [dr, dc] = DIRECTIONS[action];
    const nr = this.r + dr;
    const nc = this.c + dc;
    // Out of bounds: treated as a discovered-barrier bump (edge is always known).
    if (nr < 0 || nr >= GRID || nc < 0 || nc >= GRID) {
      return { reward: R_BUMP_DISCOVERED, done: this.tickDone() };
    }
    const cell = this.truth[nr * GRID + nc];
    if (cell === STONE) {
      // Bumping a stone. Already discovered -> punished; brand-new -> free.
      this.scan();
      if (this.discoveredStones.has(nr * GRID + nc)) {
        return { reward: R_BUMP_DISCOVERED, done: this.tickDone() };
      }
      return { reward: R_EXPLORE, done: this.tickDone() };
    }
    // Move succeeds.
    this.r = nr;
    this.c = nc;
    if (cell === GOLD) {
      this.truth[nr * GRID + nc] = FREE;
      this.goldLeft--;
      this.scan();
      return { reward: R_GOLD, done: this.tickDone() };
    }
    // FREE: pure exploration, no reward.
    this.scan();
    return { reward: R_EXPLORE, done: this.tickDone() };
  }

  private tickDone(): boolean {
    this.done = this.goldLeft <= 0 || this.steps >= this.maxSteps;
    return this.done;
  }
}
